import type { EnabledMfrrProductionUnit, MfrrBid, MfrrMarket, MfrrMarketResult } from 'api/mfrr/mfrr.types'
import {
  EAM_QUARTER_MINUTES,
  getEnabledMfrrProductionUnitKey,
  MILLISECONDS_PER_MINUTE,
  MINUTES_PER_HOUR,
} from './MfrrBidTable.helper'
import type {
  MfrrDirectionalHourVolumes,
  MfrrDirectionalViolations,
  MfrrEamMarketResultViolation,
  MfrrValueField,
  MfrrViolationRow,
  MfrrViolationUnit,
} from './MfrrBidTable.types'

const HOURS_PER_DAY = 24
const QUARTERS_PER_HOUR = MINUTES_PER_HOUR / EAM_QUARTER_MINUTES
const MILLISECONDS_PER_HOUR = MINUTES_PER_HOUR * MILLISECONDS_PER_MINUTE
const MILLISECONDS_PER_QUARTER = EAM_QUARTER_MINUTES * MILLISECONDS_PER_MINUTE

/** Index of the delivery slot (hour or quarter) that `time` falls into, relative to the day start. */
function getSlotIndex(time: ISODateTime, dayStartTime: number, slotMilliseconds: number): number {
  return Math.floor((new Date(time).getTime() - dayStartTime) / slotMilliseconds)
}

/** Maps a table row to its delivery hour: EAM rows are 15-minute quarters, CM rows are hours. */
export function getMarketResultHourForRow(market: MfrrMarket, rowIndex: number): number {
  return market === 'eam' ? Math.floor(rowIndex / QUARTERS_PER_HOUR) : rowIndex
}

/**
 * Maps every production unit to a per-direction lookup of hour-of-day (0-23) → accepted CM
 * volume for the given delivery day. Units without results for the day keep an empty lookup so
 * that their market-result column still renders. Results are matched to a unit by `name`, and
 * the delivery hour is derived from each result's `start_time` relative to the day's start.
 */
export function getMfrrMarketResultVolumesByUnit(
  marketResults: MfrrMarketResult[],
  units: EnabledMfrrProductionUnit[],
  dateStartTime: ISODateTime
): Map<string, MfrrDirectionalHourVolumes> {
  const unitByName = new Map(units.map((unit) => [unit.name, unit]))
  const dayStartTime = new Date(dateStartTime).getTime()
  const volumesByUnit = new Map<string, MfrrDirectionalHourVolumes>(
    units.map((unit) => [getEnabledMfrrProductionUnitKey(unit), { up: new Map(), down: new Map() }])
  )

  for (const result of marketResults) {
    const unit = result.production_unit === null ? undefined : unitByName.get(result.production_unit)

    if (!unit || !result.is_current) {
      continue
    }

    const hour = getSlotIndex(result.start_time, dayStartTime, MILLISECONDS_PER_HOUR)

    if (hour < 0 || hour >= HOURS_PER_DAY) {
      continue
    }

    const directional = volumesByUnit.get(getEnabledMfrrProductionUnitKey(unit))

    if (!directional) {
      continue
    }

    directional[result.direction].set(hour, result.volume)
  }

  return volumesByUnit
}

/**
 * The market-result column is volume-only and inserted immediately to the right of each unit
 * that has results. This produces the ordered value fields for a single direction section so
 * that the header labels, colgroup, and body cells stay aligned.
 */
export function getMfrrValueFieldsPerDirection(
  units: EnabledMfrrProductionUnit[],
  marketResultUnitKeys: Set<string>
): MfrrValueField[] {
  const fields: MfrrValueField[] = []

  for (const unit of units) {
    fields.push('volume', 'price')

    if (marketResultUnitKeys.has(getEnabledMfrrProductionUnitKey(unit))) {
      fields.push('marketResult')
    }
  }

  if (units.length > 1) {
    fields.push('volume', 'price')
  }

  return fields
}

export function getMfrrEamMarketResultViolations(
  bids: Array<Pick<MfrrBid, 'production_unit_id' | 'direction' | 'start_time' | 'volume'>>,
  dateStartTime: ISODateTime,
  volumesByUnit: Map<string, MfrrDirectionalHourVolumes>
): MfrrEamMarketResultViolation[] {
  if (volumesByUnit.size === 0) {
    return []
  }

  const dayStartTime = new Date(dateStartTime).getTime()
  const violations: MfrrEamMarketResultViolation[] = []

  for (const bid of bids) {
    if (bid.production_unit_id === undefined) {
      continue
    }

    const directional = volumesByUnit.get(bid.production_unit_id.toString())

    if (!directional) {
      continue
    }

    const quarterIndex = getSlotIndex(bid.start_time, dayStartTime, MILLISECONDS_PER_QUARTER)

    if (quarterIndex < 0) {
      continue
    }

    const hour = Math.floor(quarterIndex / QUARTERS_PER_HOUR)
    const marketVolume = directional[bid.direction].get(hour)

    if (marketVolume !== undefined && bid.volume < marketVolume && bid.production_unit_id !== undefined) {
      violations.push({
        direction: bid.direction,
        productionUnitId: bid.production_unit_id,
        startTime: bid.start_time,
        volume: bid.volume,
        acceptedVolume: marketVolume,
      })
    }
  }

  return violations.sort((first, second) => new Date(first.startTime).getTime() - new Date(second.startTime).getTime())
}

export function getMfrrViolationUnits(
  violations: MfrrEamMarketResultViolation[],
  units: EnabledMfrrProductionUnit[]
): MfrrViolationUnit[] {
  const unitIds = [...new Set(violations.map((violation) => violation.productionUnitId))]

  return unitIds.map((unitId) => ({
    id: unitId,
    name: units.find((unit) => unit.id === unitId)?.display_name ?? String(unitId),
  }))
}

export function getMfrrViolationRows(violations: MfrrEamMarketResultViolation[]): MfrrViolationRow[] {
  const rows = new Map<ISODateTime, MfrrViolationRow>()

  for (const violation of violations) {
    const row = rows.get(violation.startTime) ?? {
      startTime: violation.startTime,
      violationsByUnitId: new Map<number, MfrrDirectionalViolations>(),
    }
    const unitViolations = row.violationsByUnitId.get(violation.productionUnitId) ?? {}

    unitViolations[violation.direction] = violation
    row.violationsByUnitId.set(violation.productionUnitId, unitViolations)
    rows.set(violation.startTime, row)
  }

  return [...rows.values()].sort(
    (first, second) => new Date(first.startTime).getTime() - new Date(second.startTime).getTime()
  )
}
